/**
 * Reports CSV export (S11.1 stub).
 *
 * A self-contained, client-side CSV serializer over the rows already on
 * screen — no server round-trip, no external dependency. It is deliberately
 * minimal (the "CSV export stub" the plan calls for): the real 5-column
 * formats per the Board 5 SAMPLE panels are pending Judy (appendix B), so the
 * export mirrors whatever columns the report currently renders.
 */

export interface CsvColumn<T> {
  /** Localized header cell. */
  header: string;
  /** Cell value extractor — return a primitive; nullish renders empty. */
  value: (row: T) => string | number | null | undefined;
}

/** RFC-4180 field escaping: quote when the cell holds a comma, quote or newline. */
function escapeCell(raw: string | number | null | undefined): string {
  const text = raw === null || raw === undefined ? '' : String(raw);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/** Serialize rows to a CSV string (header row + one row per record). */
export function buildCsv<T>(columns: CsvColumn<T>[], rows: T[]): string {
  const headerLine = columns.map((col) => escapeCell(col.header)).join(',');
  const bodyLines = rows.map((row) =>
    columns.map((col) => escapeCell(col.value(row))).join(',')
  );
  return [headerLine, ...bodyLines].join('\r\n');
}

/**
 * Trigger a browser download of `content` as `filename`. Guarded for the
 * non-DOM (vitest node) environment so the pure serializer stays testable.
 */
export function downloadCsv(filename: string, content: string): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  // Prepend a BOM (U+FEFF) so Excel opens UTF-8 (report headers are bilingual).
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Serialize + download in one call (the report toolbar handler). */
export function exportReportCsv<T>(
  filename: string,
  columns: CsvColumn<T>[],
  rows: T[]
): void {
  downloadCsv(filename, buildCsv(columns, rows));
}
